import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Chip,
  Autocomplete,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  LocalOffer as LocalOfferIcon,
  Clear as ClearIcon,
  Save as SaveIcon,
  LockOutlined as LockOutlinedIcon,
  Description as DescriptionIcon,
  RefreshRounded as RefreshRoundedIcon,
} from '@mui/icons-material';
import { tienePermisosModulo, puedeAccionProductos } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader } from 'components/financepro';
import { useProductosMaterialContext } from 'context';

/** Misma fila `programa` que la pestaña en MantenimientoProductos */
const PROGRAMA_REGLAS_DESCUENTOS = 'reglasdescuentos';

/** Mismos estilos de campo que `Motores/informemotores.js` (INPUT_SX). */
const INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    height: 46,
    minHeight: 46,
    borderRadius: 1.6,
    bgcolor: '#FFFFFF',
    '& fieldset': { borderColor: FP.color.border },
    '&:hover fieldset': { borderColor: FP.color.borderStrong },
    '&.Mui-focused fieldset': { borderColor: FP.color.navy },
  },
  '& .MuiInputLabel-root': { fontSize: '0.86rem' },
};

const outlineBtnSx = {
  borderColor: FP.color.border,
  color: FP.color.textPrimary,
  bgcolor: '#FFFFFF',
  borderRadius: 1.6,
  textTransform: 'none',
  fontWeight: 700,
  '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
};

const primaryBtnSx = {
  bgcolor: FP.color.navy,
  color: '#FFFFFF',
  borderRadius: 1.4,
  boxShadow: 'none',
  textTransform: 'none',
  fontWeight: 700,
  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
  '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
};

const cardShellSx = {
  borderRadius: 2.5,
  border: `1px solid ${FP.color.border}`,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  overflow: 'hidden',
};

const encabezadoSeccionSx = {
  px: { xs: 2, md: 3 },
  py: 1.6,
  borderBottom: `1px solid ${FP.color.border}`,
  bgcolor: '#FAFBFE',
  display: 'flex',
  alignItems: 'center',
  gap: 1.2,
};

const iconoTituloSx = {
  width: 30,
  height: 30,
  borderRadius: 1.2,
  bgcolor: FP.color.accentBlueSoft,
  color: FP.color.accentBlue,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const formHeaderTitleSx = {
  fontWeight: 700,
  color: FP.color.textPrimary,
  fontSize: '0.98rem',
};

const cellHeadSx = {
  fontWeight: 700,
  fontSize: '0.75rem',
  color: FP.color.textSecondary,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  display: 'block',
  mb: 0.5,
};

const formLabelSx = { color: FP.color.textSecondary, fontWeight: 600 };

const ReglasDescuentos = () => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { nombreNegocio, roleDetalles, usuarioLog, claveLog } = controller;

  const puedeLeer = tienePermisosModulo(roleDetalles, PROGRAMA_REGLAS_DESCUENTOS);
  const puedeModificar = puedeAccionProductos(roleDetalles, PROGRAMA_REGLAS_DESCUENTOS, 'modificar');
  const puedeEliminar = puedeAccionProductos(roleDetalles, PROGRAMA_REGLAS_DESCUENTOS, 'borrar');
  
  const [reglasDescuentos, setReglasDescuentos] = useState([]);
  const [productos, setProductos] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [dialogoAbierto, setDialogoAbierto] = useState(false);
  const [modoEdicion, setModoEdicion] = useState(false);
  const [reglaEditando, setReglaEditando] = useState(null);
  
  // Estados del formulario
  const [tipoAplicacion, setTipoAplicacion] = useState('producto'); // 'producto' o 'departamento'
  const [productoSeleccionado, setProductoSeleccionado] = useState(null);
  const [departamentoSeleccionado, setDepartamentoSeleccionado] = useState('');
  const [tipoDescuento, setTipoDescuento] = useState('porcentaje'); // 'porcentaje' o '2x1'
  const [porcentajeDescuento, setPorcentajeDescuento] = useState('');
  const [cargando, setCargando] = useState(false);
  const [localidades, setLocalidades] = useState([]);
  /** 'todas' | 'una' — solo aplica cuando tipoAplicacion === 'producto' */
  const [alcanceLocalidad, setAlcanceLocalidad] = useState('todas');
  /** id de localidad (mismo valor que en tabla inventario) cuando alcance es 'una' */
  const [localidadRegla, setLocalidadRegla] = useState('');

  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const productosUnicosPorCodigo = useCallback((lista) => {
    const mapa = new Map();
    (lista || []).forEach((p) => {
      if (p?.codigo != null && !mapa.has(p.codigo)) {
        mapa.set(p.codigo, p);
      }
    });
    return [...mapa.values()];
  }, []);

  const cargarLocalidades = useCallback(async () => {
    if (!selectedCompany?.db_empresa) return;
    try {
      const response = await fetch(servidor + '/api/productosbackend_obtenerlocalidades', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });
      if (response.ok) {
        const data = await response.json();
        setLocalidades(data.localidades || []);
      }
    } catch (e) {
      console.error('Error al cargar localidades:', e);
    }
  }, [selectedCompany, usuarioLog, claveLog, servidor]);

  const cargarProductos = useCallback(
    async (opts = {}) => {
      if (!selectedCompany?.db_empresa) return;
      const { localidadId } = opts;
      try {
        const body = {
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tabla_inventario: 'productosnoserializados',
        };
        if (localidadId) {
          body.localidad = localidadId;
        }
        const response = await fetch(servidor + '/api/productosbackend_listarproductos', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });

        if (response.ok) {
          const data = await response.json();
          const raw = data.productos || [];
          setProductos(localidadId ? raw : productosUnicosPorCodigo(raw));
        }
      } catch (error) {
        console.error('Error al cargar productos:', error);
      }
    },
    [selectedCompany, usuarioLog, claveLog, servidor, productosUnicosPorCodigo]
  );

  const cargarDepartamentos = useCallback(async () => {
    if (!selectedCompany?.db_empresa) return;
    try {
      const response = await fetch(servidor + '/api/productosbackend_obtenerdepartamentos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setDepartamentos(data.departamentos || []);
      }
    } catch (error) {
      console.error('Error al cargar departamentos:', error);
    }
  }, [selectedCompany, usuarioLog, claveLog, servidor]);

  const cargarReglasDescuentos = useCallback(async () => {
    if (!selectedCompany?.db_empresa) return;
    setCargando(true);
    try {
      const response = await fetch(servidor + '/api/productosbackend_obtenerreglasdescuentos', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tabla_inventario: "productosnoserializados",
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setReglasDescuentos(data.reglas || []);
      } else {
        const errorData = await response.json();
        showError(errorData.Error || 'Error al cargar reglas de descuentos');
      }
    } catch (error) {
      showError('Error de conexión al cargar reglas de descuentos');
    } finally {
      setCargando(false);
    }
  }, [selectedCompany, usuarioLog, claveLog, servidor, showError]);

  useEffect(() => {
    if (!selectedCompany?.db_empresa || !puedeLeer) return;
    cargarReglasDescuentos();
    cargarDepartamentos();
    cargarLocalidades();
  }, [
    selectedCompany?.db_empresa,
    puedeLeer,
    cargarReglasDescuentos,
    cargarDepartamentos,
    cargarLocalidades,
  ]);

  useEffect(() => {
    if (!selectedCompany?.db_empresa || !puedeLeer) return;
    cargarProductos();
  }, [selectedCompany?.db_empresa, puedeLeer, cargarProductos]);

  useEffect(() => {
    if (!dialogoAbierto || tipoAplicacion !== 'producto') return;
    if (!selectedCompany?.db_empresa || !puedeLeer) return;
    if (alcanceLocalidad === 'una' && localidadRegla) {
      cargarProductos({ localidadId: localidadRegla });
    } else if (alcanceLocalidad === 'todas') {
      cargarProductos();
    }
  }, [
    dialogoAbierto,
    tipoAplicacion,
    alcanceLocalidad,
    localidadRegla,
    cargarProductos,
    selectedCompany?.db_empresa,
    puedeLeer,
  ]);

  const textoLocalidad = (idLoc) => {
    const s = String(idLoc || '').trim();
    if (!s) return '—';
    const loc = localidades.find((l) => String(l.id) === s);
    return loc?.texto || s;
  };

  const etiquetaAlcanceRegla = (regla) => {
    if (regla.tipoAplicacion !== 'producto') return '—';
    const alc = regla.alcanceLocalidad || 'todas';
    if (alc === 'una' && regla.localidad) {
      return textoLocalidad(regla.localidad);
    }
    return 'Todas las localidades';
  };

  const handleAbrirDialogo = (regla = null) => {
    if (!puedeModificar) {
      showError('No tiene permiso para crear o modificar reglas de descuentos.');
      return;
    }
    if (regla) {
      setModoEdicion(true);
      setReglaEditando(regla);
      setTipoAplicacion(regla.tipoAplicacion || 'producto');
      setTipoDescuento(regla.tipoDescuento || 'porcentaje');
      setPorcentajeDescuento(regla.porcentaje ? String(regla.porcentaje) : '');

      if (regla.tipoAplicacion === 'producto' && regla.codigoProducto) {
        const alc = regla.alcanceLocalidad === 'una' ? 'una' : 'todas';
        setAlcanceLocalidad(alc);
        setLocalidadRegla(alc === 'una' ? String(regla.localidad || '').trim() : '');
        setDepartamentoSeleccionado('');
        const producto = productos.find((p) => p.codigo === regla.codigoProducto);
        setProductoSeleccionado(
          producto || { codigo: regla.codigoProducto, descripcion: regla.descripcionProducto || '' }
        );
      } else if (regla.tipoAplicacion === 'departamento') {
        setDepartamentoSeleccionado(regla.departamento || '');
        setProductoSeleccionado(null);
        setAlcanceLocalidad('todas');
        setLocalidadRegla('');
      }
    } else {
      setModoEdicion(false);
      setReglaEditando(null);
      setTipoAplicacion('producto');
      setTipoDescuento('porcentaje');
      setPorcentajeDescuento('');
      setProductoSeleccionado(null);
      setDepartamentoSeleccionado('');
      setAlcanceLocalidad('todas');
      setLocalidadRegla('');
    }
    setDialogoAbierto(true);
  };

  const handleCerrarDialogo = () => {
    setDialogoAbierto(false);
    setModoEdicion(false);
    setReglaEditando(null);
    setTipoAplicacion('producto');
    setTipoDescuento('porcentaje');
    setPorcentajeDescuento('');
    setProductoSeleccionado(null);
    setDepartamentoSeleccionado('');
    setAlcanceLocalidad('todas');
    setLocalidadRegla('');
    cargarProductos();
  };

  const handleGuardar = async () => {
    if (!puedeModificar) {
      showError('No tiene permiso para guardar reglas de descuentos.');
      return;
    }
    // Validaciones
    if (tipoAplicacion === 'producto' && !productoSeleccionado) {
      showError('Debe seleccionar un producto');
      return;
    }

    if (tipoAplicacion === 'producto' && alcanceLocalidad === 'una' && !String(localidadRegla || '').trim()) {
      showError('Seleccione la localidad o use “Todas las localidades”');
      return;
    }
    
    if (tipoAplicacion === 'departamento' && !departamentoSeleccionado) {
      showError('Debe seleccionar un departamento');
      return;
    }
    
    if (tipoDescuento === 'porcentaje' && (!porcentajeDescuento || parseFloat(porcentajeDescuento) <= 0 || parseFloat(porcentajeDescuento) > 100)) {
      showError('El porcentaje debe estar entre 1 y 100');
      return;
    }

    try {
      const regla = {
        tipoAplicacion,
        tipoDescuento,
        porcentaje: tipoDescuento === 'porcentaje' ? parseFloat(porcentajeDescuento) : null,
        codigoProducto: tipoAplicacion === 'producto' ? productoSeleccionado.codigo : null,
        descripcionProducto: tipoAplicacion === 'producto' ? productoSeleccionado.descripcion : null,
        departamento: tipoAplicacion === 'departamento' ? departamentoSeleccionado : null,
        alcanceLocalidad: tipoAplicacion === 'producto' ? alcanceLocalidad : undefined,
        localidad: tipoAplicacion === 'producto' && alcanceLocalidad === 'una' ? String(localidadRegla).trim() : undefined,
      };

      if (modoEdicion && reglaEditando) {
        regla.id = reglaEditando.id;
        if (reglaEditando.tipoAplicacion === 'producto' && reglaEditando.codigoProducto) {
          regla.codigoProductoOriginal = reglaEditando.codigoProducto;
        }
      }

      const response = await fetch(servidor + '/api/productosbackend_guardarregladescuento', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tabla_inventario: "productosnoserializados",
          regla,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        showSuccess(data.mensaje || 'Regla de descuento guardada exitosamente');
        handleCerrarDialogo();
        cargarReglasDescuentos();
      } else {
        const errorData = await response.json();
        showError(errorData.Error || 'Error al guardar la regla de descuento');
      }
    } catch (error) {
      showError('Error de conexión al guardar');
    }
  };

  const handleEliminar = async (regla) => {
    if (!puedeEliminar) {
      showError('No tiene permiso para eliminar reglas de descuentos.');
      return;
    }
    if (!window.confirm('¿Está seguro de eliminar esta regla de descuento?')) {
      return;
    }

    try {
      const response = await fetch(servidor + '/api/productosbackend_eliminarregladescuento', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tabla_inventario: "productosnoserializados",
          reglaId: regla.id,
          codigoProducto: regla.codigoProducto,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        showSuccess(data.mensaje || 'Regla de descuento eliminada exitosamente');
        cargarReglasDescuentos();
      } else {
        const errorData = await response.json();
        showError(errorData.Error || 'Error al eliminar la regla de descuento');
      }
    } catch (error) {
      showError('Error de conexión al eliminar');
    }
  };

  if (!selectedCompany?.db_empresa) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader title="Reglas de descuentos" subtitle="Seleccione una empresa activa" showQuickActions={false} />
        <Box sx={{ p: { xs: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{
              maxWidth: 520,
              width: '100%',
              ...cardShellSx,
              p: { xs: 3, md: 4 },
              textAlign: 'center',
            }}
          >
            <DescriptionIcon sx={{ fontSize: 48, color: FP.color.textMuted, mb: 2 }} />
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, mb: 1 }}>
              Sin empresa seleccionada
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }}>
              Elija una empresa en la barra superior para administrar reglas de descuentos del inventario.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        <PageHeader title="Reglas de descuentos" subtitle="Permisos · Acceso restringido" showQuickActions={false} />
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: { xs: 2, sm: 3, md: 4 } }}>
          <Card
            elevation={0}
            sx={{
              maxWidth: 560,
              width: '100%',
              ...cardShellSx,
              textAlign: 'center',
              p: { xs: 3, md: 4 },
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso de lectura del programa «reglasdescuentos» en roles (módulo Productos).
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  const subtitleHeader =
    nombreNegocio ? `${nombreNegocio} · Mantenimiento de productos` : 'Mantenimiento de productos';

  /** Mismas anchos para cabecera y cuerpo de la tabla (igual que en ListadoClientesSuplidores). */
  const mostrarAcciones = puedeModificar || puedeEliminar;
  const reglasAnchosColumnas = mostrarAcciones
    ? ['12%', '30%', '20%', '14%', '10%', '14%']
    : ['14%', '36%', '22%', '16%', '12%'];
  const reglasColgroup = (
    <colgroup>
      {reglasAnchosColumnas.map((w, i) => (
        <col key={i} style={{ width: w }} />
      ))}
    </colgroup>
  );
  const reglasTableSx = {
    tableLayout: 'fixed',
    width: '100%',
    minWidth: { xs: 720, sm: 880 },
  };

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      <PageHeader
        title="Reglas de descuentos"
        subtitle={subtitleHeader}
        cta={
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              startIcon={<RefreshRoundedIcon />}
              onClick={() => cargarReglasDescuentos()}
              disabled={cargando}
              sx={{
                borderColor: FP.color.border,
                color: FP.color.textPrimary,
                bgcolor: '#FFFFFF',
                borderRadius: 1.4,
                px: 2,
                height: 38,
                textTransform: 'none',
                fontWeight: 700,
                '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
              }}
            >
              Actualizar
            </Button>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => handleAbrirDialogo()}
              disabled={!puedeModificar}
              sx={primaryBtnSx}
            >
              Nueva regla
            </Button>
          </Box>
        }
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Card elevation={0} sx={cardShellSx}>
          <Box sx={encabezadoSeccionSx}>
            <Box sx={iconoTituloSx}>
              <LocalOfferIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={formHeaderTitleSx}>Listado</Typography>
          </Box>

          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            {cargando ? (
              <Typography align="center" sx={{ py: 4, color: FP.color.textSecondary }}>
                Cargando…
              </Typography>
            ) : reglasDescuentos.length === 0 ? (
              <Typography align="center" sx={{ py: 4, color: FP.color.textSecondary }}>
                No hay reglas de descuentos configuradas.
              </Typography>
            ) : (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  border: `1px solid ${FP.color.border}`,
                  borderRadius: 2,
                  overflow: 'auto',
                }}
              >
                <Table sx={reglasTableSx}>
                  {reglasColgroup}
                  <TableBody>
                    <TableRow
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        '& td': {
                          border: 'none',
                          borderBottom: `2px solid ${FP.color.border}`,
                          bgcolor: FP.color.surfaceAlt,
                        },
                      }}
                    >
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Aplicación
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Producto / departamento
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Localidad
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Tipo descuento
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography component="span" sx={cellHeadSx}>
                          Valor
                        </Typography>
                      </TableCell>
                      {mostrarAcciones && (
                        <TableCell align="center" className="no-print">
                          <Typography
                            component="span"
                            sx={{ ...cellHeadSx, textAlign: 'center', width: '100%' }}
                          >
                            Acciones
                          </Typography>
                        </TableCell>
                      )}
                    </TableRow>
                    {reglasDescuentos.map((regla, index) => (
                      <TableRow key={regla.id || index} hover>
                        <TableCell align="left">
                          <Chip
                            label={regla.tipoAplicacion === 'producto' ? 'Producto' : 'Departamento'}
                            size="small"
                            sx={
                              regla.tipoAplicacion === 'producto'
                                ? { bgcolor: FP.color.accentBlueSoft, color: FP.color.accentBlue, fontWeight: 600 }
                                : { bgcolor: FP.color.infoSoft, color: FP.color.info, fontWeight: 600 }
                            }
                          />
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={
                            regla.tipoAplicacion === 'producto'
                              ? `${regla.codigoProducto} - ${regla.descripcionProducto}`
                              : regla.departamento
                          }
                        >
                          {regla.tipoAplicacion === 'producto'
                            ? `${regla.codigoProducto} - ${regla.descripcionProducto}`
                            : regla.departamento}
                        </TableCell>
                        <TableCell
                          align="left"
                          sx={{
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                          title={etiquetaAlcanceRegla(regla)}
                        >
                          {etiquetaAlcanceRegla(regla)}
                        </TableCell>
                        <TableCell align="left">
                          <Chip
                            label={regla.tipoDescuento === 'porcentaje' ? 'Porcentaje' : '2x1'}
                            size="small"
                            sx={
                              regla.tipoDescuento === 'porcentaje'
                                ? { bgcolor: FP.color.infoSoft, color: FP.color.info, fontWeight: 600 }
                                : { bgcolor: FP.color.successSoft, color: FP.color.successDark, fontWeight: 600 }
                            }
                          />
                        </TableCell>
                        <TableCell align="left">
                          {regla.tipoDescuento === 'porcentaje' ? `${regla.porcentaje}%` : '2x1'}
                        </TableCell>
                        {mostrarAcciones && (
                          <TableCell align="center" className="no-print">
                            {puedeModificar && (
                              <IconButton
                                size="small"
                                onClick={() => handleAbrirDialogo(regla)}
                                aria-label="Editar regla"
                                sx={{
                                  color: FP.color.accentBlue,
                                  '&:hover': { bgcolor: FP.color.accentBlueSoft },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            )}
                            {puedeEliminar && (
                              <IconButton
                                size="small"
                                onClick={() => handleEliminar(regla)}
                                aria-label="Eliminar regla"
                                sx={{
                                  color: FP.color.danger,
                                  '&:hover': { bgcolor: FP.color.dangerSoft },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </CardContent>
        </Card>
      </Box>

      {/* Diálogo para crear/editar regla */}
      <Dialog
        open={dialogoAbierto}
        onClose={handleCerrarDialogo}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1.5,
            px: 3,
            py: 2,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, minWidth: 0 }}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <LocalOfferIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, fontSize: '1rem', color: FP.color.textPrimary }}>
              {modoEdicion ? 'Editar regla de descuento' : 'Nueva regla de descuento'}
            </Typography>
          </Box>
          <IconButton
            onClick={handleCerrarDialogo}
            size="small"
            sx={{
              color: FP.color.textSecondary,
              border: `1px solid ${FP.color.border}`,
              borderRadius: 1.2,
              width: 32,
              height: 32,
              '&:hover': { bgcolor: FP.color.surfaceAlt, color: FP.color.navy },
            }}
            aria-label="Cerrar"
          >
            <ClearIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 3, overflow: 'visible' }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12}>
              <FormLabel component="legend" sx={formLabelSx}>Aplicar a:</FormLabel>
              <RadioGroup
                row
                value={tipoAplicacion}
                onChange={(e) => {
                  setTipoAplicacion(e.target.value);
                  setProductoSeleccionado(null);
                  setDepartamentoSeleccionado('');
                  setAlcanceLocalidad('todas');
                  setLocalidadRegla('');
                }}
              >
                <FormControlLabel value="producto" control={<Radio />} label="Producto Específico" />
                <FormControlLabel value="departamento" control={<Radio />} label="Departamento" />
              </RadioGroup>
            </Grid>

            {tipoAplicacion === 'producto' && (
              <>
                <Grid item xs={12}>
                  <FormLabel component="legend" sx={formLabelSx}>Alcance de la regla (inventario)</FormLabel>
                  <RadioGroup
                    row
                    value={alcanceLocalidad}
                    onChange={(e) => {
                      const v = e.target.value;
                      setAlcanceLocalidad(v);
                      setProductoSeleccionado(null);
                      if (v === 'todas') {
                        setLocalidadRegla('');
                      }
                    }}
                  >
                    <FormControlLabel value="todas" control={<Radio />} label="Todas las localidades" />
                    <FormControlLabel value="una" control={<Radio />} label="Solo una localidad" />
                  </RadioGroup>
                </Grid>
                {alcanceLocalidad === 'una' && (
                  <Grid item xs={12}>
                    <FormControl fullWidth sx={INPUT_SX}>
                      <InputLabel id="rd-localidad-label">Localidad</InputLabel>
                      <Select
                        labelId="rd-localidad-label"
                        value={localidadRegla}
                        onChange={(e) => {
                          setLocalidadRegla(e.target.value);
                          setProductoSeleccionado(null);
                        }}
                        label="Localidad"
                      >
                        <MenuItem value="">
                          <em>Seleccione localidad</em>
                        </MenuItem>
                        {localidades.map((loc) => (
                          <MenuItem key={loc.id} value={String(loc.id)}>
                            {loc.texto || `${loc.id}`}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <Autocomplete
                    options={productos}
                    getOptionLabel={(option) => `${option.codigo} - ${option.descripcion || ''}`}
                    value={productoSeleccionado}
                    onChange={(e, newValue) => setProductoSeleccionado(newValue)}
                    disabled={alcanceLocalidad === 'una' && !localidadRegla}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Seleccionar producto"
                        InputLabelProps={{ shrink: true }}
                        sx={INPUT_SX}
                        placeholder={
                          alcanceLocalidad === 'una' && !localidadRegla
                            ? 'Primero elija la localidad…'
                            : 'Buscar producto…'
                        }
                      />
                    )}
                  />
                </Grid>
              </>
            )}

            {tipoAplicacion === 'departamento' && (
              <Grid item xs={12}>
                <FormControl fullWidth sx={INPUT_SX}>
                  <InputLabel id="rd-departamento-label">Departamento</InputLabel>
                  <Select
                    labelId="rd-departamento-label"
                    value={departamentoSeleccionado}
                    onChange={(e) => setDepartamentoSeleccionado(e.target.value)}
                    label="Departamento"
                  >
                    <MenuItem value="">Seleccione un departamento</MenuItem>
                    {departamentos.map((dept) => (
                      <MenuItem key={dept.id} value={dept.nombre || dept.texto}>
                        {dept.nombre || dept.texto}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12}>
              <FormLabel component="legend" sx={formLabelSx}>Tipo de descuento</FormLabel>
              <RadioGroup
                row
                value={tipoDescuento}
                onChange={(e) => {
                  setTipoDescuento(e.target.value);
                  if (e.target.value === '2x1') {
                    setPorcentajeDescuento('');
                  }
                }}
              >
                <FormControlLabel value="porcentaje" control={<Radio />} label="Porcentaje" />
                <FormControlLabel value="2x1" control={<Radio />} label="2x1" />
              </RadioGroup>
            </Grid>

            {tipoDescuento === 'porcentaje' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Porcentaje de descuento"
                  type="number"
                  value={porcentajeDescuento}
                  onChange={(e) => setPorcentajeDescuento(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={INPUT_SX}
                  inputProps={{ min: 1, max: 100, step: 0.01 }}
                  helperText="Ingrese un valor entre 1 y 100"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: `1px solid ${FP.color.border}`, gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: 0 }} />
          <Button variant="outlined" onClick={handleCerrarDialogo} sx={outlineBtnSx}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={handleGuardar}
            disabled={!puedeModificar}
            sx={primaryBtnSx}
          >
            {modoEdicion ? 'Modificar' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReglasDescuentos;
